import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import {
  Yup as y,
  handleValidationError,
  validateWithSchema,
} from '@/services/yup'
import { company } from '@/consts/types'
import { handleSaveCompany } from '@/store/modules/admin/companies/reducer'
import { Form, FormTextField, FormRadioGroup } from '@/components/CoForm'
import { ComboboxCompanyTypes } from '@/components/Combobox'
import { Dialog } from '@/components/Dialog'
import { TabNavigation } from '@/components/TabNavigation'

import { StyledButton } from '../styles'
import { FormWrapper, Container } from './styles'
import { Box, InputAdornment } from '@material-ui/core'
import { toMask, unmask } from '@/services/masks'
import { cnpj, telefoneFixo } from '@/consts/regexp'
import { Phone } from 'phosphor-react'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const DEFAULT_COMPANY_TYPE = company.TRANSPORTADORA

const initialFormData = {
  wee_tipo: DEFAULT_COMPANY_TYPE,
  cod_empresa: '',
  wee_email: '',
  wee_razao_social: '',
  dsc_empresa: '',
  emp_cidade: '',
  dsc_uf: '',
  wee_ativo: 'inactive',
}

// --------------- 𝕌𝕥𝕚𝕝𝕤 ---------------

const schema = y.object().shape({
  wee_tipo: y.string().required('é obrigatório'),
  cod_empresa: y.string().required('é obrigatório'),
  wee_email: y.string().required('é obrigatório'),
  wee_razao_social: y.string().required('é obrigatório'),
  dsc_empresa: y.string().required('é obrigatório'),
  emp_cidade: y.string().required('é obrigatório'),
  dsc_uf: y.string().required('é obrigatório'),
  wee_ativo: y.string().required('é obrigatório'),
  wee_telefone: y.string().required('é obrigatório'),
})

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export function FormEdit({ current: webCompany, onClose, ...props }) {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.admin.companies.loading)

  const formRef = useRef(null)

  const isEditing = Boolean(webCompany.id)

  const title = isEditing ? webCompany.company.wee_razao_social : 'Nova empresa'

  function handleSave() {
    validateWithSchema(
      schema,
      formRef.current.values,
      (data) =>
        dispatch(
          handleSaveCompany({
            formValues: {
              wee_tipo: data.wee_tipo,
              cod_empresa: data.cod_empresa,
              wee_email: data.wee_email,
              wee_razao_social: data.wee_razao_social,
              dsc_empresa: unmask(data.dsc_empresa || ''),
              emp_cidade: data.emp_cidade,
              dsc_uf: data.dsc_uf,
              wee_telefone: data.wee_telefone,
              wee_ativo: data.wee_ativo,
            },
            webCompany,
          })
        ),
      handleValidationError(formRef.current)
    )
  }

  useEffect(() => {
    if (webCompany.id) {
      formRef.current.setData({
        wee_tipo: webCompany.wee_tipo,
        cod_empresa: webCompany.cod_empresa,
        wee_email: webCompany.wee_email,
        wee_razao_social: webCompany.wee_razao_social,
        dsc_empresa: unmask(webCompany.dsc_empresa || ''),
        emp_cidade: webCompany.emp_cidade,
        dsc_uf: webCompany.dsc_uf,
        wee_telefone: webCompany.wee_telefone,
        wee_ativo: webCompany.wee_ativo,
      })
    }
  }, [webCompany])

  return (
    <Form formRef={formRef} initialData={initialFormData} schema={schema}>
      <Dialog
        {...props}
        fullWidth
        title={title}
        actions={
          <>
            <StyledButton
              label="Cancelar"
              variant="outlined"
              color="primary"
              onClick={onClose}
            />
            <StyledButton
              loading={loading}
              label="Salvar"
              variant="contained"
              color="primary"
              onClick={handleSave}
            />
          </>
        }
      >
        <TabNavigation>
          <Container>
            <FormWrapper>
              <ComboboxCompanyTypes
                label="Tipo"
                name="wee_tipo"
                // disabled={isEditing}
                inputProps={{
                  size: 'medium',
                  variant: 'filled',
                }}
              />
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gridGap="1rem"
              >
                <FormTextField
                  name="cod_empresa"
                  label="Código"
                  size="medium"
                  variant="outlined"
                  inputMode="numeric"
                />
                <FormTextField
                  name="wee_razao_social"
                  label="Razão Social"
                  size="medium"
                  variant="outlined"
                />
                <FormTextField
                  name="dsc_empresa"
                  label="CNPJ"
                  size="medium"
                  variant="outlined"
                  textMaskProps={toMask(cnpj.array)}
                />
                <FormTextField
                  name="wee_email"
                  label="E-mail"
                  type="email"
                  size="medium"
                  variant="outlined"
                />
                <FormTextField
                  fullWidth
                  name="emp_cidade"
                  label="Cidade"
                  size="medium"
                  variant="outlined"
                />
                <FormTextField
                  fullWidth
                  name="dsc_uf"
                  label="UF"
                  size="medium"
                  variant="outlined"
                  textMaskProps={toMask([/[A-Z]/, /[A-Z]/])}
                />
                <FormTextField
                  fullWidth
                  name="wee_telefone"
                  label="Contato"
                  size="medium"
                  variant="outlined"
                  textMaskProps={toMask(telefoneFixo.array)}
                  //
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="large" weight="fill" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box maxWidth={150} gridColumn="span 2">
                  <FormRadioGroup
                    name="wee_ativo"
                    label="Status"
                    options={[
                      { value: 'active', label: 'Ativo' },
                      { value: 'inactive', label: 'Inativo' },
                    ]}
                  />
                </Box>
              </Box>
            </FormWrapper>
          </Container>
        </TabNavigation>
      </Dialog>
    </Form>
  )
}

FormEdit.propTypes = {
  current: PropTypes.object,
}

export default FormEdit
